import { useLocale, useFormatter, useTranslations } from "next-intl";
import { applyDataTestId } from "@/utils/testUtils";
import pom from "@/utils/pom";
import { Hours } from "@/dto/hours";
import { useLocalizedDays } from "@/utils/useLocalizedDays";
import { convertTimeStringToDate } from "@/utils/dateTimeUtils";

interface OperatingHoursProps {
  businessHours: Hours;
  timezone: any;
  isBusinessHourAvailable: boolean;
}

export const OperatingHours = ({
  businessHours,
  timezone,
  isBusinessHourAvailable,
}: OperatingHoursProps) => {
  const t = useTranslations("OperatingHours");
  const daysOfWeek = useLocalizedDays();
  const format = useFormatter();
  const locale = useLocale();

  if (!isBusinessHourAvailable) {
    return (
      <div
        data-testid={applyDataTestId(pom.merchantFooter.operatingHoursSection)}
        className={[
          "flex",
          "flex-col",
          "w-[312px]",
          "max-md:ml-0",
          "justify-center",
          "items-start",
          "flex gap-4 justify-start items-start",
          "max-md:px-5",
        ].join(" ")}
      >
        {t("noBusinessHours")}
      </div>
    );
  }

  const now = new Date();
  const dayIndex = now.getDay();
  const currentDayKey = daysOfWeek[dayIndex];

  return (
    <div
      data-testid={applyDataTestId(pom.merchantFooter.operatingHoursSection)}
      className="flex flex-col w-[320px] max-md:ml-0 max-md:w-full max-md:px-5 gap-5"
    >
      <p className="text-[20px] leading-[25px] font-semibold">{t("hours")}</p>

      <div
        data-testid={applyDataTestId(pom.footer.businessHours)}
        className="w-full flex flex-col gap-2"
      >
        {daysOfWeek.map((dayObj) => {
          const slots = businessHours[dayObj.key as keyof Hours];
          const safeSlots = slots || [];
          const isToday = dayObj.key === currentDayKey.key;

          return (
            <div
              key={dayObj.key}
              className={`flex flex-row justify-between ${
                isToday ? "font-bold" : ""
              }`}
            >
              <span className="capitalize pr-4 text-[15px] leading-[22px]">
                {dayObj.label}
              </span>

              <div className="flex flex-col text-end">
                {safeSlots.length > 0 ? (
                  safeSlots.map((timeSlot) => {
                    const startLuxon = convertTimeStringToDate(
                      timeSlot.start,
                      timezone
                    );
                    const endLuxon = convertTimeStringToDate(
                      timeSlot.end,
                      timezone
                    );

                    const timeOptions = {
                      hour: "numeric" as const,
                      minute: "numeric" as const,
                      timeZone: timezone,
                    };

                    const start = format.dateTime(startLuxon.toJSDate(), timeOptions);
                    const end = format.dateTime(endLuxon.toJSDate(), timeOptions);

                    const timezoneLabel =
                      startLuxon.setLocale(locale).offsetNameShort;

                    return (
                      <span
                        key={timeSlot.start}
                        className="text-[15px] leading-[22px] text-end"
                      >
                        {`${start} - ${end} ${timezoneLabel}`}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-[15px] leading-[22px]">
                    {t("closed")}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const formatTime = (time: any) => {
  const hours = Math.floor(time / 100);
  const minutes = time % 100;
  const period = hours >= 12 && hours < 24 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  return `${formattedHours}:${formattedMinutes} ${period}`;
};
